// ** Angular Imports
import { Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { Router, Routes, UrlTree } from '@angular/router';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';

// ** Third-Party Imports
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { provideTranslocoScope, TranslocoService } from '@jsverse/transloco';

// ** Custom Component Imports
import { PrivacyComponent } from './shared/components/pages/tcs/privacy/privacy.component';
import { CertificationsComponent } from './shared/components/pages/tcs/certifications/certifications.component';


// Define the shared routes that will be reused in both default and language-specific paths
const sharedRoutes = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/home-two/home-two.component').then((m) => m.HomeTwoComponent),
    title: 'SEO.HOME.TITLE',
    data: {
      META_DESCRIPTION: 'SEO.HOME.DESCRIPTION',
    },
    providers: [
      provideTranslocoScope({
        alias: 'HOME',
        scope: 'home-two',
      }),
    ],
  },
  {
    path: 'animation-demo',
    loadComponent: () =>
      import('./pages/home-two/home-two.component').then((m) => m.HomeTwoComponent),
    providers: [
      provideTranslocoScope({
        alias: 'HOME',
        scope: 'home-two',
      }),
    ],
  },
  {
    path: 'static-demo',
    loadComponent: () =>
      import('./pages/home-static/home-static.component').then((m) => m.HomeStaticComponent),
    providers: [
      provideTranslocoScope({
        alias: 'HOME',
        scope: 'home-static',
      }),
    ],
  },
  {
    path: 'about-us',
    loadComponent: () =>
      import('./pages/about/about-us/about-us.component').then(
        (m) => m.AboutUsComponent
      ),
    title: 'SEO.ABOUT_US.TITLE',
    data: {
      META_DESCRIPTION: 'SEO.ABOUT_US.DESCRIPTION',
    },
    providers: [
      provideTranslocoScope({
        alias: 'ABOUT_US',
        scope: 'about',
      }),
    ],
  },
  {
    path: 'our-culture',
    loadComponent: () =>
      import('./pages/about/our-culture/our-culture.component').then(
        (m) => m.OurCultureComponent
      ),
    title: 'SEO.OUR_CULTURE.TITLE',
    data: {
      META_DESCRIPTION: 'SEO.OUR_CULTURE.DESCRIPTION',
    },
    providers: [
      provideTranslocoScope({
        alias: 'OUR_CULTURE',
        scope: 'our-culture',
      }),
    ],
  },
  {
    path: 'contact-us',
    loadComponent: () =>
      import('./pages/about/contact-us/contact-us.component').then(
        (m) => m.ContactUsComponent
      ),
    title: 'SEO.CONTACT_US.TITLE',
    data: {
      META_DESCRIPTION: 'SEO.CONTACT_US.DESCRIPTION',
    },
  },
  {
    path: 'products',
    children: [
      {
        path: 'account',
        children: [
          {
            path: 'sme-account',
            loadComponent: () =>
              import('./pages/products/account/sme-acc/account.component').then(
                (m) => m.AccountComponent
              ),
            title: 'SEO.IBAN_BUSINESS_ACCOUNT.TITLE',
            data: {
              META_DESCRIPTION: 'SEO.IBAN_BUSINESS_ACCOUNT.DESCRIPTION',
            },
            providers: [
              provideTranslocoScope({
                alias: 'BANK_ACCOUNT',
                scope: 'bank-account',
              }),
            ],
          },
          {
            path: 'freelancer-account',
            loadComponent: () =>
              import('./pages/products/account/freelancer-acc/freelancer-account.component').then(
                (m) => m.FreelancerAccountComponent
              ),
            title: 'SEO.FREELANCER_ACCOUNT.TITLE',
            data: {
              FAQ_TRANSLATION_KEY: 'FREELANCER_ACCOUNT',
            },
            providers: [
              provideTranslocoScope({
                alias: 'FREELANCER_ACCOUNT',
                scope: 'freelancer-acc',
              }),
            ],
          },
          {
            path: 'multi-user-account',
            loadComponent: () =>
              import('./pages/products/account/multi-user-acc/multi-user-account.component').then(
                (m) => m.MultiUserAccountComponent
              ),
            title: 'SEO.MULTI_USER_ACCOUNT.TITLE',
            data: {
              FAQ_TRANSLATION_KEY: 'MULTI_USER_ACCOUNT',
            },
            providers: [
              provideTranslocoScope({
                alias: 'MULTI_USER_ACCOUNT',
                scope: 'multi-user-acc',
              }),
            ],
          }
        ],
      },
      {
        path: 'soft-pos',
        loadComponent: () =>
          import('./pages/products/soft-pos/soft-pos.component').then(
            (m) => m.SoftPosComponent
          ),
        title: 'SEO.PHONE_POS.TITLE',
        data: {
          META_DESCRIPTION: 'SEO.PHONE_POS.DESCRIPTION',
        },
        providers: [
          provideTranslocoScope({
            alias: 'SOFTPOS',
            scope: 'soft-pos',
          }),
        ],
      },
      {
        path: 'financing',
        loadComponent: () =>
          import('./pages/products/financing/financing.component').then(
            (m) => m.FinancingComponent
          ),
        title: 'SEO.FINANCING.TITLE',
        data: {
          META_DESCRIPTION: 'SEO.FINANCING.DESCRIPTION',
        },
        providers: [
          provideTranslocoScope({
            alias: 'FINANCING',
            scope: 'financing',
          }),
        ],
      },
      {
        path: 'restaurant-pos',
        loadComponent: () =>
          import(
            './pages/products/restaurant-pos/restaurant-pos.component'
          ).then((m) => m.RestaurantPosComponent),
        title: 'SEO.RESTAURANT_POS.TITLE',
        data: {
          META_DESCRIPTION: 'SEO.RESTAURANT_POS.DESCRIPTION',
        },
        providers: [
          provideTranslocoScope({
            alias: 'RESTAURANTPOS',
            scope: 'restaurant-pos',
          }),
        ],
      },
      {
        path: 'expense-management',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/products/expense-management/expense-management.component').then(
                (m) => m.ExpenseManagementComponent
              ),
            title: 'SEO.EXPENSE_MANAGEMENT.TITLE',
            data: {
              META_DESCRIPTION: 'SEO.EXPENSE_MANAGEMENT.DESCRIPTION',
            },
            providers: [
              provideTranslocoScope({
                alias: 'EXPENSE_MANAGEMENT',
                scope: 'expense-management',
              }),
            ],
          },
          {
            path: 'control',
            loadComponent: () =>
              import(
                './pages/landing/products/expense-management/expense-management.component'
              ).then((m) => m.ExpenseManagementComponent),
            providers: [
              provideTranslocoScope({
                alias: 'EXPENSE_LANDING',
                scope: 'expense-landing',
              }),
            ],
          },
        ],
      },      
      {
        path: 'payments',
        children: [
          {
            path: 'best-pos-saudi',
            loadComponent: () =>
              import(
                './pages/landing/products/payments/payment-terminal/payment-terminal.component'
              ).then((m) => m.PaymentTerminalComponent),
              providers: [
                provideTranslocoScope({
                  alias: 'POS_LANDING',
                  scope: 'pos-landing',
                }),
              ],
          },
          {
            path: 'payment-terminal',
            loadComponent: () =>
              import(
                './pages/products/payments/payment-terminal/payment-terminal.component'
              ).then((m) => m.PaymentTerminalComponent),
            title: 'SEO.PAYMENT_TERMINAL.TITLE',
            data: {
              META_DESCRIPTION: 'SEO.PAYMENT_TERMINAL.DESCRIPTION',
            },
          },
          {
            path: 'local-transfers',
            loadComponent: () =>
              import('./pages/products/payments/local-transfers/local-transfers.component').then(
                (m) => m.LocalTransfersComponent
              ),
            title: 'SEO.LOCAL_TRANSFERS.TITLE',
            data: {
              FAQ_TRANSLATION_KEY: 'LOCAL_TRANSFERS',
            },
            providers: [
              provideTranslocoScope({
                alias: 'LOCAL_TRANSFERS',
                scope: 'local-transfers',
              })
            ],
          },
          {
            path: 'sadad',
            loadComponent: () =>
              import(
                './pages/products/payments/sadad/sadad.component'
              ).then((m) => m.SadadComponent),
            title: 'SEO.SADAD.TITLE',
            data: {
              FAQ_TRANSLATION_KEY: 'SADAD',
            },
            providers: [
              provideTranslocoScope({
                alias: 'SADAD',
                scope: 'sadad',
              }),
            ],
          },
          {
            path: 'hala-debit-card',
            loadComponent: () =>
              import(
                './pages/products/payments/hala-debit-card/hala-debit-card.component'
              ).then((m) => m.HalaDebitCardComponent),
            title: 'SEO.HALA_DEBIT_CARD.TITLE',
            data: {
              FAQ_TRANSLATION_KEY: 'HALA_DEBIT_CARD',
            },
            providers: [
              provideTranslocoScope({
                alias: 'HALA_DEBIT_CARD',
                scope: 'hala-debit-card',
              }),
            ],
          },
          {
            path: 'wallet-to-wallet',
            loadComponent: () =>
              import(
                './pages/products/payments/wallet-to-wallet/wallet-to-wallet.component'
              ).then((m) => m.WalletToWalletComponent),
            title: 'SEO.WALLET_TO_WALLET.TITLE',
            data: {
              FAQ_TRANSLATION_KEY: 'WALLET_TO_WALLET',
            },
            providers: [
              provideTranslocoScope({
                alias: 'WALLET_TO_WALLET',
                scope: 'wallet-to-wallet',
              }),
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'best-pos-saudi',
    loadComponent: () =>
      import(
        './pages/landing/products/payments/payment-terminal/payment-terminal.component'
      ).then((m) => m.PaymentTerminalComponent),
      providers: [
        provideTranslocoScope({
          alias: 'POS_LANDING',
          scope: 'pos-landing',
        }),
      ],
  },
  {
    path: 'linking-pos',
    loadComponent: () =>
      import(
        './pages/funnel/linking/linking-pos/linking-pos.component'
      ).then((m) => m.LinkingPosComponent),
      providers: [
        provideTranslocoScope({
          alias: 'LINKING_POS',
          scope: 'linking-pos',
        }),
      ],
  },
  {
    path: 'linking-card',
    loadComponent: () =>
      import(
        './pages/funnel/linking/linking-card/linking-card.component'
      ).then((m) => m.LinkingCardComponent),
      providers: [
        provideTranslocoScope({
          alias: 'LINKING_CARD',
          scope: 'linking-card',
        }),
      ],
  },
];

export const routes: Routes = [
  // Root path - always English
  {
    path: '',
    redirectTo: languageRedirectFunction,
    pathMatch: 'full',
  },
  // Explicit language paths
  {
    path: ':lang',
    loadComponent: () =>
      import('./layout/layout.component').then((m) => m.LayoutComponent),
    children: sharedRoutes,
  },
  // Terms and conditions routes
  {
    path: ':lang/terms-conditions',
    children: [
      {
        path: 'loyalty',
        loadComponent: () =>
          import(
            './shared/components/pages/tcs/loyalty-program/loyalty-program.component'
          ).then((m) => m.LoyaltyProgramComponent),
      },
      {
        path: 'payroll',
        loadComponent: () =>
          import('./shared/components/pages/tcs/payroll/payroll.component').then(
            (m) => m.Payroll
          ),
      },
      {
        path: 'nbi',
        loadComponent: () =>
          import('./shared/components/pages/tcs/nbi/nbi.component').then(
            (m) => m.NBIComponent
          ),
      },
      {
        path: 'select-type',
        loadComponent: () =>
          import('./shared/components/pages/tcs/select-tcs/select-tcs.component').then(
            (m) => m.SelectTCSComponent
          ),
      },
      {
        path: 'privacy-policy',
        component: PrivacyComponent,
        data: {
          titleEn: 'Privacy Policy',
          titleAr: 'الخصوصية',
        },
      },
      {
        path: 'certifications',
        component:CertificationsComponent,
        data: {
          titleEn: 'Certifications Statement',
          titleAr: 'بيان الشهادات'
        },
      },
    ],
  },
  {
    path: ':lang/drop-off',
    loadComponent: () =>
      import('./layout/private/private-layout.component').then(
        (m) => m.PrivateLayoutComponent
      ),
    children: [
      {
        path: 'freelancer',
        loadComponent: () =>
          import('./pages/funnel/drop-off/freelancer/freelancer.component').then(
            (m) => m.FreelancerDropoffComponent
          ),
      },
      {
        path: 'cr',
        loadComponent: () =>
          import('./pages/funnel/drop-off/cr/cr.component').then(
            (m) => m.CRDropoffComponent
          ),
      },
    ],
  },
  {
    path: 'customer-document-upload/:id',
    loadComponent: () =>
      import(
        './shared/components/pages/document-upload/customer-document-upload/customer-document-upload.component'
      ).then((m) => m.CustomerDocumentUploadComponent),
  },
  {
    path: '**',
    redirectTo: languageRedirectFunction,
  },
];

@Injectable()
export class TranslatedTitleStrategy extends TitleStrategy {
  private translateService = inject(TranslocoService);
  private readonly title = inject(Title);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const title = this.buildTitle(snapshot);
    if (title) {
      this.translateService
        .selectTranslateObject(title)
        .subscribe((translatedTitle) => {
          this.title.setTitle(translatedTitle);
        });
    } else {
      this.title.setTitle('Hala');
    }
  }
}

export function languageRedirectFunction(): string | UrlTree {
  const cookieService = inject(SsrCookieService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  const language = cookieService.get('language') || 'ar';

  if (!cookieService.get('language')) {
    cookieService.set('language', language, 0, '/');
  }

  const queryParams: Record<string, string> = {};

  // Parse query params from window.location manually
  if (isPlatformBrowser(platformId)) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });
  }

  return router.createUrlTree([`/${language}`], {
    queryParams,
  });
}